import React from "react";
import { Heart, Users2, Sparkles } from "lucide-react";

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-padding bg-[#F4F7F6]"
    >
      <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow" data-testid="about-eyebrow">Our Story</div>
          <h2
            data-testid="about-heading"
            className="font-display text-3xl md:text-5xl font-semibold mt-3 aegis-navy-text leading-tight"
          >
            Built from a family story —
            <span className="block text-[#3D7A73]">for every Alberta family.</span>
          </h2>

          <div
            data-testid="about-avatar-card"
            className="mt-8 rounded-2xl bg-white p-6 border border-gray-100 shadow-sm"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full aegis-navy-bg text-white flex items-center justify-center font-display font-bold text-xl flex-shrink-0">
                A
              </div>
              <div>
                <div className="font-display font-semibold aegis-navy-text">
                  Founder, Aegis Care Operations Ltd.
                </div>
                <div className="text-sm text-gray-500 mt-0.5">
                  Edmonton, Alberta · Est. 2025
                </div>
              </div>
            </div>
            <blockquote
              data-testid="about-pull-quote"
              className="mt-5 text-gray-700 italic border-l-4 border-[#3D7A73] pl-4 leading-relaxed"
            >
              "I started Aegis Care because no one told us this kind of help
              existed when my dad needed it most. I don't want another family to
              feel that alone."
            </blockquote>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="space-y-6 text-gray-700 text-lg leading-relaxed" data-testid="about-story">
            <p>
              Aegis Care Operations Ltd. began with a deeply personal chapter in
              my own family's life. When my father became unwell, our family
              struggled — not because we didn't want to help, but because we
              didn't know help like this existed. No one sat us down and
              explained that compassionate, in-home support was possible in
              Alberta. My dad passed away in 2025 without ever receiving the
              care he deserved at home. That gap — between families who need
              help and families who simply don't know it's available — is the
              reason this company exists.
            </p>
            <p>
              Soon after, when my mother broke her arm, our family experienced
              the other side of that story. Caregivers walked into our home not
              as strangers, but as steady, kind people. They cooked meals, sat
              and talked with her, helped her through the small daily things
              that matter most when life slows down. They lifted her mood as
              much as they lifted her schedule. It changed everything for our
              family — and showed me what good home care really looks like.
            </p>
            <p>
              I founded Aegis Care to bring that same dignity, conversation, and
              warmth to other Alberta families. To make sure people know this
              resource is here. To take pressure off adult children juggling
              work and worry. And to make sure seniors feel seen — not just
              served.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-5" data-testid="about-values">
            <Value
              icon={Heart}
              title="Compassion first"
              text="Care that treats every client like our own parent — patient, kind, and present."
            />
            <Value
              icon={Users2}
              title="Family in the loop"
              text="Clear communication and check-ins so the family is never left wondering."
            />
            <Value
              icon={Sparkles}
              title="Dignity always"
              text="From morning routines to mealtime conversations — dignity is non-negotiable."
            />
          </div>

          <a
            href="#booking"
            data-testid="about-cta"
            className="btn-primary mt-10"
          >
            Start a conversation with us
          </a>
        </div>
      </div>
    </section>
  );
};

const Value = ({ icon: Icon, title, text }) => (
  <div className="rounded-xl bg-white p-5 border border-gray-100">
    <div className="w-10 h-10 rounded-lg bg-[#E3EEEC] text-[#2A5C56] flex items-center justify-center mb-3">
      <Icon size={18} aria-hidden="true" />
    </div>
    <div className="font-display font-semibold aegis-navy-text">{title}</div>
    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{text}</p>
  </div>
);
